import React, { FunctionComponent, useState, useEffect, memo } from 'react';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';

import Button from '../../../UI/Button';
import Modal from '../../../UI/Modal';
import Icon from '../../../Icons';
import Tabs from '../../../UI/Tab';
import { VehicleTag } from '../../Listing/dataMapping';
import InfinityScroll from '../../../InfinityScroll';
import useInfiniteScroll from '../../../InfinityScroll/scrollHook';
import { dataMapping } from './dataMapping';
import { parsePrice } from '../../../../utils/utils';

import { Container, ModalTitle, ButtonList, DoubleIconButton } from './styles';
import { sendEvent2 } from '../../../../utils/tracking';

interface Props {
  updateParams: Function;
  dispatchToStoreFetchTxnOverview: Function;
  downloadVehicleTransactions: Function;
}

const tabs = [{ name: 'All' }, { name: 'Debit' }, { name: 'Credit' }];

const tabType = {
  0: '',
  1: 'DEBIT',
  2: 'CREDIT',
};

const Transactions: FunctionComponent<Props> = memo(({ updateParams, downloadVehicleTransactions }) => {
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(0);
  const [downloadModalState, setDownloadModalState] = useState(false);

  const summary = useSelector((state: any) => state.fasTagDetail.overview);
  const transactions = useSelector((state: any) => _get(state, 'fasTagDetail.transactionData.list', []));
  const totalTransactions = useSelector((state: any) => _get(state, 'fasTagDetail.transactionData.totalCount', []));
  const searchText: string = useSelector((state: any) => state.fasTag.searchText);
  const isLoaded: boolean = useSelector((state: any) => state.fasTagDetail.loaded);
  const vehicleId = useSelector((state: any) => _get(state, 'fasTagDetail.vehicleData.vehicleId', ''));
  const vehicleData = useSelector((state: any) => _get(state, 'fasTagDetail.vehicleData', {}));

  const [, setIsFetching] = useInfiniteScroll(fetchMoreTransactions);

  useEffect(() => {
    if (vehicleId) {
      updateParams({ searchText, vehicleId, reset: true, pageNo: 0 });
    }
    setIsFetching(false);
    // TODO: handle dependencies 'searchText', 'setIsFetching', and 'updateParams'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  const transactionType: any = tabType;

  const handleTabChange = (event: any, value: number) => {
    setIsFetching(false);
    const selectedTab = transactionType[value];
    sendEvent2(
      'tab_btn',
      {
        event_action: 'click',
        event_category: 'vehicle_txn_details',
        screen_name: 'ft_vehicle_txn_details',
        miscellaneous: JSON.stringify({
          tab: selectedTab === '' ? 'all' : selectedTab,
        }),
        target_product: 'fastag',
        event_platform: 'web',
      },
      true,
    );
    setTabValue(value);
    setPage((prevState) => 0);
    updateParams({
      pageNo: 0,
      transactionType: selectedTab,
      reset: true,
    });
  };

  function fetchMoreTransactions() {
    if (transactions.length < totalTransactions) {
      const pageNo = page + 1;
      setPage((prevState) => pageNo);
      updateParams({
        pageNo,
        transactionType: transactionType[tabValue],
        reset: false,
      });
      setIsFetching(false);
    }
  }

  const toggleDownloadModal = () => {
    sendEvent2(
      'pop_up_view',
      {
        event_action: 'view',
        event_category: 'download_report_pop_up',
        screen_name: 'ft_vehicle_txn_details',
        target_product: 'fastag',
        event_platform: 'web',
      },
      true,
    );
    sendEvent2(
      'download_report_btn',
      {
        event_action: 'click',
        event_category: 'top_nav',
        screen_name: 'ft_vehicle_txn_details',
        target_product: 'fastag',
        event_platform: 'web',
      },
      true,
    );
    setDownloadModalState((prevState) => !prevState);
  };

  const closeDownloadModal = () => {
    sendEvent2(
      'close_btn',
      {
        event_action: 'click',
        event_category: 'download_report_pop_up',
        screen_name: 'ft_vehicle_txn_details',
        target_product: 'fastag',
        event_platform: 'web',
      },
      true,
    );
    setDownloadModalState(false);
  };

  const downloadReport = (reportFormat: 'xlsx' | 'pdf') => {
    sendEvent2(
      reportFormat === 'xlsx' ? 'download_excel_btn' : 'download_pdf_btn',
      {
        event_action: 'click',
        event_category: 'download_report_pop_up',
        screen_name: 'ft_vehicle_txn_details',
        target_product: 'fastag',
        event_platform: 'web',
      },
      true,
    );
    downloadVehicleTransactions(reportFormat);
    closeDownloadModal();
  };

  const { vehicleNumber, txnCount, totalAmount, serialNumber } = summary;
  const dataList: any = dataMapping({});
  const { tagClass, fastagColor } = vehicleData;

  return (
    <Container>
      {vehicleNumber ? (
        <>
          <div className="flex justify-content-fe summary falign-c bg-white relative">
            <div className="mr16">
              <span className="font-light-grey">Total Expense :</span>
              <span className="fwb ml5">{parsePrice({ price: totalAmount })}</span>
              <span className="ml16 mr16 font-greyish">|</span>
              <span className="font-light-grey">Transactions :</span>
              <span className="fwb ml5">{txnCount || '---'}</span>
              <span className="ml16 mr16 font-greyish"> | </span>
              <span className="font-light-grey small">Barcode :</span>
              <span className="ml5">{serialNumber}</span>
              <span className="ml16 mr16 font-greyish"> | </span>
              <span className="mr5 font-light-grey small">Tag Class :</span>
              <span className="ib-t">
                <VehicleTag tagClass={tagClass} fastagColor={fastagColor} font="medium" />
              </span>
            </div>
            <div className="flex falign-c">
              <div className="mr16">
                <Button variant="outlined" icon="download" clickHandler={toggleDownloadModal}>
                  Report
                </Button>
              </div>
            </div>
          </div>
          <div>
            <Tabs handleChange={handleTabChange} value={tabValue} tabs={tabs} />
          </div>
          <div>
            <InfinityScroll componentList={dataList} componentData={transactions} loading={isLoaded} />
          </div>
          <Modal isOpen={downloadModalState} onClose={closeDownloadModal}>
            <div className="flex flex-dir-c falign-s">
              <ModalTitle>Select download format of Fastag Report of {vehicleNumber}.</ModalTitle>
              <ButtonList>
                <DoubleIconButton onClick={() => downloadReport('xlsx')}>
                  <div className="left">
                    <Icon name="xls" />
                    Excel Format
                  </div>
                  <div className="right">
                    <Icon name="download-blue" />
                  </div>
                </DoubleIconButton>
                <DoubleIconButton onClick={() => downloadReport('pdf')}>
                  <div className="left">
                    <Icon name="pdf" />
                    PDF Format
                  </div>
                  <div className="right">
                    <Icon name="download-blue" />
                  </div>
                </DoubleIconButton>
              </ButtonList>
            </div>
          </Modal>
        </>
      ) : null}
    </Container>
  );
});

export default Transactions;
